import posthog from 'posthog-js';

/**
 * A no-op when VITE_POSTHOG_KEY isn't set at build time — posthog.init() is
 * never called, and posthog-js's own methods are safe to call regardless
 * (they just do nothing pre-init). Starts opted out of capturing regardless
 * of key: CookieBanner is what actually turns tracking on, once a manager
 * has said yes — this only gets the SDK loaded and ready to be opted in.
 */
export function initAnalytics(): void {
  const key = import.meta.env.VITE_POSTHOG_KEY;
  if (!key) return;

  posthog.init(key, {
    api_host: import.meta.env.VITE_POSTHOG_HOST ?? 'https://eu.i.posthog.com',
    opt_out_capturing_by_default: true,
    // person_profiles: 'identified_only' — Sentry.setUser and posthog.identify
    // are both deliberately given a bare user id only, never email or name;
    // this keeps PostHog from creating a full profile for a merely-anonymous
    // pre-consent visit.
    person_profiles: 'identified_only',
  });
}

/** Called once a manager accepts the cookie banner. */
export function optIntoAnalytics(): void {
  posthog.opt_in_capturing();
}

/** Called on decline, and available if a manager changes their mind later. */
export function optOutOfAnalytics(): void {
  posthog.opt_out_capturing();
}

export function hasAnalyticsConsent(): boolean {
  return posthog.has_opted_in_capturing();
}

/** Ties autocaptured click/page events to the same person the backend's
 * usage events (user_registered, squad_analysed, ...) are keyed by — see
 * apps/api/src/lib/analytics.ts. A bare user id only, matching that file's
 * "no PII in the analytics tool" choice. */
/** `email` is set as a person property (PostHog's `$set`), never used as the
 * distinct id itself — it's there so a person is recognizable in the
 * PostHog UI, not so events are keyed by it. Still never sent to Sentry. */
export function identifyUser(userId: string, email?: string): void {
  posthog.identify(userId, email ? { email } : undefined);
}

/** Called on logout, so a shared device's next (different) user doesn't
 * inherit the previous person's identity for their anonymous pre-login
 * activity. */
export function resetIdentity(): void {
  posthog.reset();
}
